import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator } from "@/modern/components/ui/dropdown-menu";
import { MoreVertical, Archive, Link2, Copy, Download, Upload, Bell, Trash2 } from "lucide-react";
import { useEntityChecklistContext } from "../context/EntityChecklistContext";
import { useRef, useState } from "react";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { NudgeUsersModal } from "./editors/NudgeUsersModal";

export const ChecklistMenu = () => {
  const {
    selectedChecklist,
    toggleArchiveChecklist,
    copyChecklistLink,
    duplicateChecklistRecord,
    importChecklistItems,
    exportChecklistItems,
    deleteChecklist,
    handleHideChecklistDetails,
    handleCloseEditor,
    updateChecklist,
    newChecklist,
    isCreatingNewChecklist,
    canEditSelectedChecklist
  } = useEntityChecklistContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isNudgeModalOpen, setIsNudgeModalOpen] = useState(false);
  const canEdit = canEditSelectedChecklist;

  const checklist = newChecklist || selectedChecklist;
  if (!checklist) return null;

  const handleArchive = () => {
    if (!checklist.archived) {
      const selectedBoard = getSavedBoardSelection();
      const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
      if (!canShowArchivedRecords) {
        handleHideChecklistDetails();
        handleCloseEditor();
      }
    }
    toggleArchiveChecklist(checklist);
  };

  const handleCopyLink = () => {
    copyChecklistLink(checklist);
  };

  const handleDuplicate = () => {
    duplicateChecklistRecord(checklist);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importChecklistItems(checklist, file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExport = () => {
    exportChecklistItems(checklist);
  };

  const handleNudge = () => {
    setIsNudgeModalOpen(true);
  };

  const handleDelete = async () => {
    await deleteChecklist(checklist)
    handleHideChecklistDetails();
    handleCloseEditor();
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".txt" style={{ display: "none" }} onChange={handleFileChange} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" disabled={!!newChecklist || isCreatingNewChecklist}>
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={handleArchive} disabled={!canEdit}>
            <Archive className="size-4" />
            {checklist.archived ? "Unarchive" : "Archive"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyLink}>
            <Link2 className="size-4" />
            Copy Link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate} disabled={!canEdit}>
            <Copy className="size-4" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleImport} disabled={!canEdit}>
            <Download className="size-4" />
            Import Checklist Items
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExport} disabled={!canEdit}>
            <Upload className="size-4" />
            Export Checklist Items
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleNudge} disabled={!canEdit}>
            <Bell className="size-4" />
            Nudge Users
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDelete} disabled={!canEdit} className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300 focus:bg-red-50 dark:focus:bg-red-950/30">
            <Trash2 className="size-4" />
            Delete Checklist
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Nudge Users Modal */}
      <NudgeUsersModal
        open={isNudgeModalOpen}
        onOpenChange={setIsNudgeModalOpen}
        checklist={checklist}
        onUpdateChecklist={updateChecklist}
      />
    </>
  );
};
